import React, {
  cloneElement,
  forwardRef,
  ForwardRefExoticComponent,
  Fragment,
  isValidElement,
  ReactNode,
  RefAttributes,
  RefObject,
  useEffect,
  useRef,
  useState
} from 'react';
import {createPortal} from 'react-dom';
import {setRef} from "@mrocket/utils";


export interface PortalProps {
  children?: ReactNode;

  /**
   * Ссылка на контейнер куда будет размещен портал.
   *
   * Если в качестве значение будет указан `inplace`,
   * то содержимое будет показано без использования портала
   *
   * @default RefObject<HTMLBodyElement>
   */
  containerRef?: RefObject<HTMLElement>;

  /**
   * Сохраняет содержимое в документе после закрытия
   *
   * @default true
   */
  keepMounted?: boolean;

  disablePortal?: boolean;

  /**
   * Флаг, отвечающий за показ содержимого в документе
   */
  visible?: boolean;

}

const canUseDOM = () => {
  return (
    typeof window !== 'undefined' &&
    typeof window.document !== 'undefined' &&
    typeof window.document.createElement !== 'undefined'
  );
};

const canUseDom = canUseDOM();
const defaultContainer = canUseDom ? document.body : null;

const Portal = forwardRef<HTMLElement, PortalProps>((
  props,
  ref,
) => {
  const {
    visible,
    keepMounted = true,
    children,
    containerRef,
    disablePortal
  } = props;

  const [container, setContainer] = useState(defaultContainer);

  const isWasVisibleRef = useRef(false);

  useEffect(() => {
    if (typeof containerRef === 'object') {
      setContainer(containerRef.current);
    }
  }, [containerRef]);

  useEffect(() => {
    if (container && !disablePortal) {
      setRef(ref, container);
      return () => {
        setRef(ref, null);
      };
    }
    return undefined;
  }, [ref, container, disablePortal]);

  isWasVisibleRef.current = !!visible;

  const shouldClientRender = canUseDom && (visible || (keepMounted && isWasVisibleRef.current));
  const shouldServerRender = !canUseDom && visible;

  if (!shouldClientRender && !shouldServerRender) {
    return null;
  }

  if (disablePortal) {
    if (isValidElement(children)) {
      const newProps = {
        ref: ref,
      };
      return cloneElement(children, newProps);
    }

    return <Fragment>{children}</Fragment>;
  }

  return container ? createPortal(children, container) : null;
}) as ForwardRefExoticComponent<PortalProps & RefAttributes<Element>>;

export {Portal}
